import React, { useMemo } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import NvIcon from 'shared/components/nv-icon';
import { AdminUser } from 'redux/schemas/models/help-options';
import { CommunityCompletionUser } from 'redux/schemas/models/user';
import { config } from '@config/pendo.config.json';
import NvEmoji from 'shared/components/nv-emoji';
import { getCourseAliases } from 'redux/selectors/course';
import AskNovoAIButton from './ask-novo-ai-button';
import QuestionText from './question-text';
import { responseDrawerState } from '../styles';
import { AskNovoAiState } from '../helpers';

interface ErrorDrawerStateProps {
  question?: string;
  teachingTeam: AdminUser[];
  isProcessingFilesInProgress: boolean;
  onClick: () => void;
  setSendMessage: React.Dispatch<React.SetStateAction<boolean>>;
}

const ErrorDrawerState = ({
  question,
  teachingTeam,
  isProcessingFilesInProgress,
  onClick,
  setSendMessage,
}: ErrorDrawerStateProps) => {
  const askNovoAi: AskNovoAiState = useSelector(
    (state) => state.app.askNovoAi,
  );

  const aliases = useSelector((state) => getCourseAliases(state));
  const networkIssues = askNovoAi?.response?.responseCode === 500;

  const recipients: CommunityCompletionUser[] = teachingTeam
    .filter((item) => item.isQnaFacilitator)
    .map((user) => ({
      id: user.user.id,
      firstName: user.user.firstName,
      lastName: user.user.lastName,
      email: user.user.email,
      avatarUrl: '',
    }));

  const errorMessage = useMemo(() => {
    if (askNovoAi?.response?.answer?.trim()) {
      return askNovoAi?.response?.answer?.trim();
    }
    if (askNovoAi?.response?.responseCode === 500) {
      return t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.NETWORK_ERROR_MESSAGE();
    }
    if (askNovoAi?.response?.responseCode === 202) {
      if (recipients.length > 0 || !!askNovoAi.helpOptions?.hasHelpResourcesEnabled) {
        return t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.SEE_HELP_OPTIONS();
      }
      return t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.TALK_TO_TEAM(aliases?.teachingTeamAliases);
    }
    return t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.ERROR_DEFAULT_MESSAGE();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askNovoAi.helpOptions?.hasHelpResourcesEnabled, askNovoAi?.response?.answer, askNovoAi?.response?.responseCode, recipients.length]);

  return (
    <div css={responseDrawerState}>
      <NvEmoji icon='light' size='large' />
      <div className='response-container'>
        <QuestionText question={askNovoAi?.response?.questionText || question} />
        <p className='answer'>{errorMessage}</p>
        {isProcessingFilesInProgress && (
            <div className='processing-upload'>
              <p>
                {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.PROCESSING_UPLOADS_DISCLAIMER()}
              </p>
            </div>
          )}
        {askNovoAi?.helpOptions?.hasHelpResourcesEnabled && askNovoAi?.helpOptions?.courseHelp?.body && (
          <>
            <div className='divider' />
            <p>{t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.HERE_ARE_INFO()}</p>
            {/*  eslint-disable-next-line react/no-danger */}
            <div className='inline-code' dangerouslySetInnerHTML={{ __html: askNovoAi?.helpOptions?.courseHelp?.body }} />
          </>
        )}
        {!!recipients.length && (
          <>
            <div className='divider' />
            <div className='link-wrapper'>
              <NvIcon
                icon='messages'
                className='color-primary'
                size='smallest'
              />
              <button
                type='button'
                className='link'
                onClick={() => setSendMessage(true)}
                data-qa={config.pendo.aiLearning.messageTeachingTeam}
              >
                {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.MESSAGE_TEACHING(aliases?.teachingTeamAliases)}
              </button>
            </div>
          </>
        )}

        <AskNovoAIButton onClick={onClick} dataQa={networkIssues ? config.pendo.aiLearning.networkNovoAiButton : config.pendo.aiLearning.askAnotherNovoAiButton}>
          {networkIssues ? t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.TRY_AGAIN_BUTTON() : t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.ASK_ANOTHER_BUTTON()}
        </AskNovoAIButton>
      </div>
    </div>
  );
};

export default ErrorDrawerState;
